import React, { FC } from 'react';

import { Control, Controller } from 'react-hook-form';

import { Autocomplete, Box, Grid, TextField } from '@mui/material';

interface CategoryTagSelectorProps {
  promoTagArrays: App.PromoTagArrays | null;
  promoTagSelections: {
    selectedDepartment: string | null;
    selectedCategory: string | null;
    selectedSubCategory: string | null;
  };
  setPromoTagSelection: (
    deptTag: string,
    categoryTag?: string,
    subCategoryTag?: string,
  ) => {
    selectedDepartment: string | null;
    selectedCategory: string | null;
    selectedSubCategory: string | null;
  };
  isPromoMetaLoading: boolean;

  // Form-specific props
  formType: 'react-hook-form' | 'rjsf';
  // React Hook Form props
  control?: Control<any>;
  // RJSF props
  formData?: any;
  onFormChange?: (evt: any) => void;
  // Custom input props
  allowCustomInput?: boolean;
  isErroring?: boolean;
}

const CategoryTagSelector: FC<CategoryTagSelectorProps> = ({
  promoTagArrays,
  promoTagSelections,
  setPromoTagSelection,
  isPromoMetaLoading,
  formType,
  control,
  formData,
  onFormChange,
  allowCustomInput = false,
  isErroring = false,
}) => {
  if (isPromoMetaLoading || promoTagArrays == null || promoTagArrays.categoryTags.length === 0) {
    return null;
  }

  const handleChange = (newValue: string | null) => {
    if (formType === 'rjsf' && onFormChange && formData) {
      const updatedFormData = {
        ...formData,
        category_tag: newValue,
      };

      onFormChange({
        formData: updatedFormData,
      });
    }

    setPromoTagSelection(promoTagSelections.selectedDepartment, newValue);
  };

  const renderOption = (props: any, option: string) => {
    const isAllowed = promoTagArrays.allowedCategoryTags.find(
      (tag) => tag.departmentTag === promoTagSelections.selectedDepartment && tag.categoryTag === option,
    );
    return (
      <Box
        component="li"
        {...props}
        sx={{
          color: isAllowed ? 'inherit' : 'text.disabled',
          opacity: isAllowed ? 1 : 0.6,
        }}
      >
        {option}
      </Box>
    );
  };

  if (formType === 'react-hook-form' && control) {
    return (
      <Controller
        key="category_tag"
        name="category_tag"
        control={control}
        rules={{ required: promoTagArrays.categoryTags.length > 0 }}
        render={({ field }) => (
          <Grid item xs={4}>
            <Autocomplete
              {...field}
              value={promoTagSelections.selectedCategory || ''}
              options={promoTagArrays.categoryTags}
              freeSolo={allowCustomInput}
              renderOption={renderOption}
              renderInput={(params) => (
                <TextField
                  name="category_tag"
                  required
                  {...params}
                  variant="outlined"
                  label="Category"
                  helperText={allowCustomInput ? 'Type to search or enter custom value' : undefined}
                  error={isErroring}
                />
              )}
              onChange={(e: unknown, newValue: string | null) => {
                field.onChange(newValue);
                handleChange(newValue);
              }}
            />
          </Grid>
        )}
      />
    );
  }

  // RJSF version
  return (
    <Grid item xs={4}>
      <Autocomplete
        value={promoTagSelections.selectedCategory || ''}
        options={promoTagArrays.categoryTags}
        freeSolo={allowCustomInput}
        renderOption={renderOption}
        renderInput={(params) => (
          <TextField
            name="category_tag"
            required
            {...params}
            variant="outlined"
            label="Category"
            helperText={allowCustomInput ? 'Type to search or enter custom value' : undefined}
            error={isErroring}
          />
        )}
        onChange={(e: unknown, newValue: string | null) => {
          handleChange(newValue);
        }}
      />
    </Grid>
  );
};

export default CategoryTagSelector;
